import { ApprovalMode } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import {
  SubStepCompletionTrigger,
  type SubStepInstance,
  SubStepStatus,
  type WorkflowCase,
  WorkflowCaseStatus,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

import { isWorkflowCaseReadonly } from '~/models/workflowCaseReadonly'

// isWorkflowCaseReadonly はアプリ横断 (DemandForecastDaily 等、遷移先関連画面
// でも同じ read-only 判定が要る) のため `~/models/workflowCaseReadonly` が SoT。
// 既存 import 経路 (`~/pages/WorkflowCaseDetail/models/workflowCaseDetail`) を
// 壊さないよう re-export する。
export { isWorkflowCaseReadonly }

/**
 * AC-WFW-007f (workflow-linked-page-completion-trigger design D2/D8): 業務
 * ワークフロー側の「完了にする」 を非活性にし、 リンク先画面の業務終端操作
 * (需要想定確定) の**自動完了だけ**で進める kind。 計画提出 (需要調達計画ほか) は
 * 提出に伴う完了に加えて手動完了も認めるので (AC-WFW-007f 改訂、 #11318)、
 * この集合に含めない。 入札系 (BIDDING_SPOT / BIDDING_INTRADAY) は「全入札完了」 の
 * system signal が存在しないため対象外 (spec AC-WFW-007f ※注)。
 *
 * 利用者は 2 つの component: サブステップの完了 switch の非活性化・文言・予約幅
 * (`SubStepRow`)、 ステッパーの「自動完了 / 手動完了」 (`WizardStepper`)。
 */
const LINKED_PAGE_COMPLETION_TRIGGER_KINDS: ReadonlySet<LinkedPageKind> =
  new Set([LinkedPageKind.DEMAND_FORECAST_DAILY])

/** サブステップがリンク先画面の業務操作で自動完了するか (AC-WFW-007f)。 */
export function isSubStepCompletedByLinkedPage(
  linkedPageKind: LinkedPageKind | undefined,
): boolean {
  return (
    linkedPageKind !== undefined &&
    LINKED_PAGE_COMPLETION_TRIGGER_KINDS.has(linkedPageKind)
  )
}

/**
 * 大ステップが自動で完了するか (ステッパーの「自動完了」 / 「手動完了」)。
 * 全サブステップが {@link isSubStepCompletedByLinkedPage} の時だけ自動完了。
 * 1 件でも人が「完了にする」 を押すサブステップがあれば手動完了 (その大ステップを
 * 進めるのに人の操作が要るため)。 サブステップ 0 件は手動完了扱い。
 */
export function isBigStepCompletedAutomatically(
  subSteps: SubStepInstance[],
): boolean {
  return (
    subSteps.length > 0 &&
    subSteps.every((ss) => isSubStepCompletedByLinkedPage(ss.linkedPageKind))
  )
}

/**
 * 大ステップが実行時ロック対象かどうかを判定するヘルパー (spec AC-WFT-018、
 * workflow-runtime-lock-display-order design)。表示順で 1 個前の大ステップが
 * 完了しているかどうかのみで判定する (判定は backend `IsBigStepUnlocked` が
 * SoT で、この関数はその写像)。
 *
 * idx は `sortedBigSteps` (表示順ソート済み配列) 内のインデックス
 * (0-origin)。idx===0 の先頭大ステップは常時アンロック (AC-01/AC-06)。
 * prevComplete は idx-1 の大ステップが {@link isBigStepComplete} で完了と
 * 判定されたかどうか (呼び出し側が事前に計算して渡す)。
 */
export function isBigStepUnlocked(idx: number, prevComplete: boolean): boolean {
  if (idx === 0) return true
  return prevComplete
}

/**
 * 大ステップの配下 substep が全て `SUB_STEP_STATUS_COMPLETE` かどうかを判定
 * するヘルパー (backend `ComputeSubStepStatus` が返す derived status を参照。
 * raw `isCompleted` は見ない — AwaitingApproval 中の substep を誤って
 * complete 扱いしないため、workflow-runtime-lock-display-order design)。
 * substep が 0 件の大ステップは false (防御。entity constructor が空
 * substep を拒否するため通常発生しない)。
 */
export function isBigStepComplete(
  subSteps: Array<Pick<SubStepInstance, 'status'>>,
): boolean {
  if (subSteps.length === 0) return false
  return subSteps.every((ss) => ss.status === SubStepStatus.COMPLETE)
}

/**
 * 閲覧者がサブステップの担当者でないか (spec AC-WFW-007e)。「完了にする」は担当者以外にも
 * 活性のまま、常時の注意表示と確認ダイアログを出す条件になる (操作は止めない。サーバーも
 * 担当者を理由に拒否しない)。
 *
 * 担当者が 0 名 (空配列 / 指定なし) の場合は対象外。1 名以上いて、操作者の memberId が
 * 担当者一覧に含まれない (未ログイン相当の undefined を含む) ときに true。
 */
export function isViewerAssigneeNotApplicable(
  assigneeMemberIds: string[],
  currentMemberId: string | undefined,
): boolean {
  if (assigneeMemberIds.length === 0) return false
  if (currentMemberId === undefined) return true
  return !assigneeMemberIds.includes(currentMemberId)
}

/**
 * アクティブ (先頭未完了) サブステップの座標 (spec AC-WFW-001 §243、
 * 2026-08-19 改訂 D5)。
 */
export type ActiveSubStep = {
  bigStepDisplayOrder: number
  subStepDisplayOrder: number
}

/**
 * アクティブ (先頭未完了) サブステップの座標を導出するヘルパー (spec
 * AC-WFW-001 §243、2026-08-19 改訂 D5: 一覧の要承認・要対応ボタンから詳細画面へ
 * 遷移した際の focus 対象)。big_step.display_order → sub_step.display_order
 * の順で走査し、`status` が COMPLETE でない最初のサブステップを返す。
 *
 * 逐次活性化の不変条件 (`workflow_case_state_derivation.go` の
 * `deriveSubStepStatus`: 大ステップは display_order gating、サブステップは
 * 直前完了までブロック) により、終端でないケースのアクティブサブステップは
 * 常に 1 つ。終端ケース (COMPLETE / DELETED、`isWorkflowCaseReadonly`) は
 * undefined を返し、呼び出し側は自動展開しない (= 従来表示のまま)。
 */
export function activeSubStepOf(
  wfCase: WorkflowCase,
): ActiveSubStep | undefined {
  if (isWorkflowCaseReadonly(wfCase)) return undefined
  const sortedBigSteps = wfCase.bigSteps
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
  for (const bs of sortedBigSteps) {
    const sortedSubSteps = bs.subSteps
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
    for (const ss of sortedSubSteps) {
      if (ss.status !== SubStepStatus.COMPLETE) {
        return {
          bigStepDisplayOrder: bs.displayOrder,
          subStepDisplayOrder: ss.displayOrder,
        }
      }
    }
  }
  return undefined
}

/** BigStep 表示ステータス (design/demand/case-detail.html `.bigstep` badge: 未着手/対応中/完了)。 */
export type BigStepDisplayStatus = 'notStarted' | 'current' | 'complete'

/**
 * BigStep の表示ステータスを判定するヘルパー (design のバッジ「未着手/対応中/完了」に対応)。
 *
 * BigStepInstance は自身のステータスを proto field に持たない (SubStepInstance と異なり
 * status は backend 未算出) ため、 frontend 側で doneCount/totalCount/isLocked から導出する
 * (= サーバー計算値の再計算ではなく、 既に確定済の completion 情報を表示用区分に写像するだけ)。
 *
 * - isLocked (= 1 個前の大ステップが未完了) → 未着手
 * - 全サブステップ完了 (かつ 1 件以上) → 完了
 * - それ以外 (unlocked かつ未完了、 0 件含む) → 対応中
 */
export function bigStepDisplayStatusOf(
  doneCount: number,
  totalCount: number,
  isLocked: boolean,
): BigStepDisplayStatus {
  if (totalCount > 0 && doneCount === totalCount) return 'complete'
  if (isLocked) return 'notStarted'
  return 'current'
}

/**
 * ワークフロー表示順で最後のサブステップかを判定するヘルパー
 * (spec AC-WFW-008 note: 全サブステップ完了によるケース自動完了後でも、
 * 最後のサブステップのみ完了取消可能)。
 */
function isLastSubStepInWorkflow(
  wfCase: WorkflowCase,
  bigStepDisplayOrder: number,
  subStepDisplayOrder: number,
): boolean {
  const sortedBigSteps = wfCase.bigSteps
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
  const lastBigStep = sortedBigSteps[sortedBigSteps.length - 1]
  if (lastBigStep == null) return false
  if (lastBigStep.displayOrder !== bigStepDisplayOrder) return false
  const sortedSubSteps = lastBigStep.subSteps
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
  const lastSubStep = sortedSubSteps[sortedSubSteps.length - 1]
  if (lastSubStep == null) return false
  return lastSubStep.displayOrder === subStepDisplayOrder
}

/**
 * 計画提出のサブステップ (リンク先が需要調達計画・発電販売計画・需要抑制計画・分割供給計画) の
 * リンク先 (spec AC-WFW-005e (d))。 計画は提出すると広域機関への提出が戻らないため、
 * 完了取消できない。 Deprecated の PLAN_SUBMISSION (提出管理の互換ページ) は含めない
 * (backend の IsPlanSubmissionPage と揃える)。
 */
const PLAN_SUBMISSION_LINKED_PAGE_KINDS: ReadonlySet<LinkedPageKind> = new Set([
  LinkedPageKind.PLAN_PROCUREMENT,
  LinkedPageKind.PLAN_GEN_SALES,
  LinkedPageKind.PLAN_DEMAND_SUPPRESSION,
  LinkedPageKind.PLAN_SPLIT_SUPPLY,
])

/** リンク先が計画提出か (backend の IsPlanSubmissionPage と揃える)。 */
function isPlanSubmissionLinkedPage(
  linkedPageKind: LinkedPageKind | undefined,
): boolean {
  return (
    linkedPageKind !== undefined &&
    PLAN_SUBMISSION_LINKED_PAGE_KINDS.has(linkedPageKind)
  )
}

/**
 * 計画提出のサブステップが、 担当者の「完了にする」 (手動完了) で完了 / 承認待ち
 * になっているか (AC-WFW-007f)。 計画の提出に伴う完了 (LINKED_OPERATION) や
 * 契機が不明な既存行 (UNSPECIFIED) は false で、 従来の表示のままにする。
 */
export function isManuallyCompletedPlanSubmission(
  subStep: Pick<SubStepInstance, 'linkedPageKind' | 'completionTrigger'>,
): boolean {
  return (
    subStep.completionTrigger === SubStepCompletionTrigger.MANUAL &&
    isPlanSubmissionLinkedPage(subStep.linkedPageKind)
  )
}

/** 完了取消できない理由。 画面は planSubmission だけ専用のツールチップを出す。 */
export type UndoBlockedReason =
  | 'deleted'
  | 'notComplete'
  | 'planSubmission'
  | 'completedCaseNotLast'
  | 'laterProgressed'

/**
 * サブステップの完了取消ができない理由を返す (取消できるなら undefined)。
 * spec AC-WFW-005e/037/038。 判定の優先順位:
 *
 * - ケースが削除済みなら不可
 * - 対象サブステップが COMPLETE でなければ不可
 * - 計画提出のサブステップなら不可 (AC-WFW-005e (d)。 後続の有無によらず
 *   「後続あり」より優先する = (b) の理由を出さない)
 * - ワークフロー順で後ろのサブステップに COMPLETE / AWAITING_APPROVAL がある場合は不可
 *   (= 最後に完了したサブステップのみ取消可能)
 * - ケースが完了済みの場合、対象がワークフロー表示順で最後のサブステップでないと不可
 *   (backend の reopenableAtChainEnd と同じ順: 後続判定が先)
 */
export function undoBlockedReason(
  wfCase: WorkflowCase,
  bigStepDisplayOrder: number,
  subStep: SubStepInstance,
): UndoBlockedReason | undefined {
  if (wfCase.status === WorkflowCaseStatus.DELETED) return 'deleted'
  if (subStep.status !== SubStepStatus.COMPLETE) return 'notComplete'
  if (isPlanSubmissionLinkedPage(subStep.linkedPageKind)) {
    return 'planSubmission'
  }

  const sortedBigSteps = wfCase.bigSteps
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)

  let passedTarget = false
  for (const big of sortedBigSteps) {
    if (big.displayOrder < bigStepDisplayOrder) continue
    const sortedSubSteps = big.subSteps
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
    for (const ss of sortedSubSteps) {
      if (
        big.displayOrder === bigStepDisplayOrder &&
        ss.displayOrder === subStep.displayOrder
      ) {
        passedTarget = true
        continue
      }
      if (!passedTarget) continue
      if (
        ss.status === SubStepStatus.COMPLETE ||
        ss.status === SubStepStatus.AWAITING_APPROVAL
      ) {
        return 'laterProgressed'
      }
    }
  }
  if (
    wfCase.status === WorkflowCaseStatus.COMPLETE &&
    !isLastSubStepInWorkflow(wfCase, bigStepDisplayOrder, subStep.displayOrder)
  ) {
    return 'completedCaseNotLast'
  }
  return undefined
}

/**
 * spec AC-WFA-006: 業務ワークフローの写しの承認フロー設定が「自動」 の需要想定
 * (単日) のサブステップか。 帰属不明の扱いの一文を出す条件で、 予測手法の有無と
 * サブステップの状態では絞らない。
 */
export function showsDemandAutoUnattributedNote(
  subStep: SubStepInstance,
): boolean {
  return (
    subStep.linkedPageKind === LinkedPageKind.DEMAND_FORECAST_DAILY &&
    subStep.approvalSettingMode === ApprovalMode.AUTO
  )
}
